import { apiClient } from "./api_base";

// ===========================================
// USER MANAGEMENT API
// ===========================================

// Get all users with pagination and search
export const getAllUsers = async (params = {}) => {
    const response = await apiClient.get('/admin/users', { params });
    return response;
};

// Get specific user by ID
export const getUserById = async (userId) => {
    const response = await apiClient.get(`/admin/users/${userId}`);
    return response;
};

// Update user information
export const updateUser = async (userId, userData) => {
    const response = await apiClient.put(`/admin/users/${userId}`, userData);
    return response;
};

// Deactivate user
export const deactivateUser = async (userId) => {
    const response = await apiClient.delete(`/admin/users/${userId}`);
    return response;
};

// Reset user identification code
export const resetUserCode = async (userId) => {
    const response = await apiClient.post(`/admin/users/${userId}/reset-code`);
    return response;
};

// Bulk user operations
export const bulkUserOperations = async (operationData) => {
    const response = await apiClient.post('/admin/users/bulk', operationData);
    return response;
};

// Get user activity logs
export const getUserActivity = async (userId) => {
    const response = await apiClient.get(`/admin/users/${userId}/activity`);
    return response;
};

// ===========================================
// SYSTEM ADMINISTRATION API
// ===========================================

// Get system statistics
export const getSystemStats = async () => {
    const response = await apiClient.get('/admin/system/stats');
    return response;
};

// Get system logs
export const getSystemLogs = async (params = {}) => {
    const response = await apiClient.get('/admin/system/logs', { params });
    return response;
};

// Clear system cache
export const clearSystemCache = async () => {
    const response = await apiClient.post('/admin/system/cache/clear');
    return response;
};

// Get system health
export const getSystemHealth = async () => {
    const response = await apiClient.get('/admin/system/health');
    return response;
};

// Create system backup
export const createSystemBackup = async () => {
    const response = await apiClient.post('/admin/system/backup');
    return response;
};

// List system backups
export const listSystemBackups = async () => {
    const response = await apiClient.get('/admin/system/backup/list');
    return response;
};

// Restore system backup
export const restoreSystemBackup = async (backupName) => {
    const response = await apiClient.post('/admin/system/backup/restore', { backup_name: backupName });
    return response;
};

// ===========================================
// ANALYTICS API
// ===========================================

// Get user analytics
export const getUserAnalytics = async (params = {}) => {
    const response = await apiClient.get('/admin/analytics/users', { params });
    return response;
};

// Get recipe analytics
export const getRecipeAnalytics = async (params = {}) => {
    const response = await apiClient.get('/admin/analytics/recipes', { params });
    return response;
};

// Get sustainability analytics
export const getSustainabilityAnalytics = async (params = {}) => {
    const response = await apiClient.get('/admin/analytics/sustainability', { params });
    return response;
};

// Get performance analytics
export const getPerformanceAnalytics = async (params = {}) => {
    const response = await apiClient.get('/admin/analytics/performance', { params });
    return response;
};

// Export reports
export const exportReports = async (params = {}) => {
    const response = await apiClient.get('/admin/analytics/reports/export', { 
        params,
        responseType: 'blob' // For file downloads
    });
    return response;
};

// Get custom reports
export const getCustomReports = async () => {
    const response = await apiClient.get('/admin/analytics/reports/custom');
    return response;
};

// Create custom report
export const createCustomReport = async (reportData) => {
    const response = await apiClient.post('/admin/analytics/reports/custom', reportData);
    return response;
};

// ===========================================
// CONTENT MANAGEMENT API
// ===========================================

// Get global categories
export const getGlobalCategories = async () => {
    const response = await apiClient.get('/admin/content/categories/global');
    return response;
};

// Update global category
export const updateGlobalCategory = async (categoryId, categoryData) => {
    const response = await apiClient.put(`/admin/content/categories/${categoryId}`, categoryData);
    return response;
};

// Delete global category
export const deleteGlobalCategory = async (categoryId) => {
    const response = await apiClient.delete(`/admin/content/categories/${categoryId}`);
    return response;
};

// Get pending recipes
export const getPendingRecipes = async (params = {}) => {
    const response = await apiClient.get('/admin/content/recipes/pending', { params });
    return response;
};

// Approve recipe
export const approveRecipe = async (recipeId) => {
    const response = await apiClient.post(`/admin/content/recipes/${recipeId}/approve`);
    return response;
};

// Reject recipe
export const rejectRecipe = async (recipeId, reason) => {
    const response = await apiClient.post(`/admin/content/recipes/${recipeId}/reject`, { reason });
    return response;
};

// Bulk content operations
export const bulkContentOperations = async (operationData) => {
    const response = await apiClient.post('/admin/content/bulk', operationData);
    return response;
};

// ===========================================
// SECURITY API
// ===========================================

// Get security dashboard
export const getSecurityDashboard = async () => {
    const response = await apiClient.get('/admin/security/dashboard');
    return response;
};

// Get audit logs
export const getAuditLogs = async (params = {}) => {
    const response = await apiClient.get('/admin/security/audit-logs', { params });
    return response;
};

// Get failed logins
export const getFailedLogins = async (params = {}) => {
    const response = await apiClient.get('/admin/security/failed-logins', { params });
    return response;
};

// Get suspicious activity
export const getSuspiciousActivity = async (params = {}) => {
    const response = await apiClient.get('/admin/security/suspicious-activity', { params });
    return response;
};

// Block user
export const blockUser = async (userId) => {
    const response = await apiClient.post(`/admin/security/block-user/${userId}`);
    return response;
};

// Unblock user
export const unblockUser = async (userId) => {
    const response = await apiClient.post(`/admin/security/unblock-user/${userId}`);
    return response;
};

// ===========================================
// SETTINGS API
// ===========================================

// Get system settings
export const getSystemSettings = async () => {
    const response = await apiClient.get('/admin/settings');
    return response;
};

// Update system settings
export const updateSystemSettings = async (settingsData) => {
    const response = await apiClient.put('/admin/settings', settingsData);
    return response;
};

// Get feature flags
export const getFeatureFlags = async () => {
    const response = await apiClient.get('/admin/settings/features');
    return response;
};

// Update feature flags
export const updateFeatureFlags = async (featuresData) => {
    const response = await apiClient.put('/admin/settings/features', featuresData);
    return response;
};

// Get integration settings
export const getIntegrationSettings = async () => {
    const response = await apiClient.get('/admin/settings/integrations');
    return response;
};

// Update integration settings
export const updateIntegrationSettings = async (integrationData) => {
    const response = await apiClient.put('/admin/settings/integrations', integrationData);
    return response;
};

// Get email settings
export const getEmailSettings = async () => {
    const response = await apiClient.get('/admin/settings/email');
    return response;
};

// Update email settings
export const updateEmailSettings = async (emailData) => {
    const response = await apiClient.put('/admin/settings/email', emailData);
    return response;
};

// Get notification settings
export const getNotificationSettings = async () => {
    const response = await apiClient.get('/admin/settings/notifications');
    return response;
};

// Update notification settings
export const updateNotificationSettings = async (notificationData) => {
    const response = await apiClient.put('/admin/settings/notifications', notificationData);
    return response;
};





